import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { friendlyError } from "@/lib/errors";

/**
 * /register-admin — HIDDEN admin bootstrap page.
 *
 * Not linked from anywhere on the public site. The URL and the bootstrap
 * secret are given only to the site owner. Anyone who guesses the URL sees
 * the form, but a submission without the correct secret returns "Not found"
 * from the backend — so the endpoint can't be brute-forced blindly and no
 * information leaks about whether the URL is real.
 *
 * On successful registration the user is logged in immediately and
 * redirected to /admin.
 */
export default function AdminRegisterPage() {
  const nav = useNavigate();
  const { setUser } = useAuth();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [secret, setSecret] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-admin", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        secret: secret.trim(),
      });
      setUser(data.user);
      toast.success("Admin account created. Welcome.");
      nav("/admin");
    } catch (err) {
      // Special case: if the email is already registered, offer to promote
      // the existing account to admin instead of failing outright. This is
      // extremely common because owners often register once via /register
      // (as user or attorney), realize they need admin, and then hit this
      // page — where "already registered" would otherwise be a dead end.
      const detail = err?.response?.data?.detail || "";
      const isAlreadyRegistered =
        typeof detail === "string" && detail.toLowerCase().includes("already registered");
      if (isAlreadyRegistered) {
        const wantPromote = window.confirm(
          `An account with ${email.trim().toLowerCase()} already exists.\n\n` +
          `Would you like to PROMOTE that existing account to admin instead? ` +
          `You'll keep your existing password — no need to re-enter it. ` +
          `Just log in with your existing credentials after this promotion.`
        );
        if (wantPromote) {
          try {
            await api.post("/auth/promote-to-admin", {
              email: email.trim().toLowerCase(),
              secret: secret.trim(),
            });
            toast.success("Account promoted to admin. Log in with your existing password.");
            nav("/login");
            return;
          } catch (promoteErr) {
            toast.error(friendlyError(promoteErr, "Promotion failed. Verify your bootstrap secret."));
            return;
          } finally {
            setBusy(false);
          }
        }
      }
      toast.error(friendlyError(err, "Registration failed. Verify your bootstrap secret and try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--bg-bone)" }}
      data-testid="admin-register-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div
            className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-3"
            style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}
          >
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="font-display font-extrabold text-2xl"
              style={{ color: "var(--text-primary)" }}>
            Create your admin account
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>
            Restricted. You need the bootstrap secret to complete this form.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="card-surface p-6 space-y-4"
          data-testid="admin-register-form"
        >
          <div>
            <label className="field-label">Your name</label>
            <input
              type="text"
              className="field-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              data-testid="admin-register-name"
            />
          </div>
          <div>
            <label className="field-label">Email</label>
            <input
              type="email"
              className="field-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              data-testid="admin-register-email"
            />
          </div>
          <div>
            <label className="field-label">Password</label>
            <div className="relative">
              <input
                type={showPwd ? "text" : "password"}
                className="field-input pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="admin-register-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: "var(--text-secondary)" }}
                data-testid="admin-register-password-toggle"
                tabIndex={-1}
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              Minimum 8 characters. Use a strong password — this is a superuser account.
            </p>
          </div>
          <div>
            <label className="field-label">Confirm password</label>
            <input
              type={showPwd ? "text" : "password"}
              className="field-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              data-testid="admin-register-password-confirm"
            />
          </div>
          <div>
            <label className="field-label">Bootstrap secret</label>
            <input
              type="password"
              className="field-input"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              required
              autoComplete="off"
              spellCheck={false}
              data-testid="admin-register-secret"
              placeholder="Provided out-of-band"
            />
            <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              Delivered separately (chat / password manager). Not shown anywhere on the site.
            </p>
          </div>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="admin-register-submit"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Creating…" : "Create admin account"}
          </button>
        </form>

        <div className="text-center mt-6 text-xs"
             style={{ color: "var(--text-secondary)" }}>
          Already have an admin account?{" "}
          <Link to="/login" className="underline"
                style={{ color: "var(--accent-copper)" }}
                data-testid="admin-register-signin-link">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
